import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

import { useConfirm } from '@app/hooks/useConfirm';

interface PromptOptions {
  hasChanges?: boolean;
  title?: string;
  subtitle?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export type Nullable<T> = T | null;

export const usePrompt = ({
  hasChanges = false,
  title = '',
  subtitle = '',
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  onConfirm,
  onCancel,
}: PromptOptions) => {
  const blocker = useBlocker(hasChanges);

  const { show } = useConfirm();

  const confirm = useCallback(async () => {
    if (!hasChanges) return true;

    return new Promise<boolean>((resolve) => {
      show({
        title,
        subtitle,
        confirmText,
        cancelText,
        onConfirm: () => {
          resolve(true);
          onConfirm?.();
        },
        onCancel: () => {
          resolve(false);
          onCancel?.();
        },
      });
    });
  }, [
    cancelText,
    confirmText,
    hasChanges,
    onCancel,
    onConfirm,
    show,
    subtitle,
    title,
  ]);

  const openConfirm = useCallback(async () => {
    if (blocker.state === 'blocked') {
      const result = await confirm();
      const action = result ? 'proceed' : 'reset';
      blocker[action]();
    }
  }, [confirm, blocker]);

  useEffect(() => {
    openConfirm();
  }, [openConfirm, blocker]);

  useEffect(() => {
    if (hasChanges) {
      window.onbeforeunload = () => subtitle;
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [hasChanges, subtitle]);

  return {
    confirm,
  };
};
